import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Zalogowano pomyslnie');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Blad logowania');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_10%,hsl(189_70%_45%/0.18),transparent_55%),radial-gradient(700px_circle_at_80%_0%,hsl(206_70%_55%/0.12),transparent_50%)]" />
        <div className="absolute inset-0 bg-card/50" />
        <div className="relative z-10 px-12 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <span className="font-heading font-bold text-3xl tracking-tight">Torr.pl</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold mb-4 text-foreground">Platforma komunikacji marketingowej</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Masowa wysylka SMS i email z mechanizmem afiliacyjnym, targetowaniem behawioralnym i wsparciem AI.
            Zarzadzaj kampaniami, kontaktami i bramkami SMS w jednym miejscu.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-border/50 bg-card/80 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-primary" />
              <span className="font-heading font-bold text-xl">Torr.pl</span>
            </div>
            <CardTitle className="font-heading text-xl">Zaloguj sie</CardTitle>
            <CardDescription>Dostep do panelu afilianta i kampanii</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" placeholder="jan@torr.pl"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required data-testid="login-email-input"
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Haslo</Label>
                <Input
                  id="password" type="password" placeholder="Min. 6 znakow"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required data-testid="login-password-input"
                  className="bg-background/50"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Zaloguj
              </Button>
            </form>
            <Separator className="my-4" />
            <p className="text-center text-sm text-muted-foreground">
              Nie masz konta?{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                Zarejestruj sie
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
